import type { LobbyClientMessage, BackgroundRoomContext, PacingMode } from '@danda/shared';
import {
  RoomTypeEnum,
  MIN_PM_BALANCE,
  LOW_BALANCE_GATE_TEXT,
  LOW_BALANCE_GATE_BUTTON_LABEL,
} from '@danda/shared';
import { usePlayerActivityStore } from '@/lib/stores/playerActivityStore';
import { usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { getStaffAvatarUrl } from '@/lib/media';

// Message types that are protocol/auto-fired (not driven by player UI). They
// MUST NOT bump the AFK intent timer or the AFK kick never fires.
const NON_INTENT_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  'ping',
  'lobby:subscribe',
  'lobby:unsubscribe',
  'lobby:request-background',
  'lobby:request-portrait',
  // Auto-fired on every socket open (connection-layer friends hydration), so it
  // must not count as player activity or it would reset the AFK timer on
  // background reconnects.
  'lobby:friends-list',
]);

// Backend lobby `dispatch_message` pre-checks PM balance for every variant
// in `LobbyClientMessage::is_ai_handler()` and rejects when below
// MIN_PM_BALANCE. Auto-fired sends (LobbyBackground's useEffect on
// roomContext, portrait requests, etc.) don't run through the ChatPanel
// gate — without this client-side mirror, the rejection error mutates the
// chat store, which re-renders the parent, which recomputes roomContext,
// which re-fires the effect → 7-15 rejections/sec storm. Keep this list
// in lockstep with the Rust `is_ai_handler` match.
const AI_HANDLER_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  'lobby:tavian-talk',
  'lobby:party-chat',
  'lobby:inner-voice-talk',
  'lobby:char-create-start',
  'lobby:char-create-continue',
  'lobby:level-up-start',
  'lobby:level-up-continue',
  'lobby:merchant-start',
  'lobby:merchant-message',
  'lobby:char-chat-start',
  'lobby:char-chat-send',
  'lobby:dm-send',
  'lobby:request-background',
  'lobby:request-portrait',
]);

// Subset that auto-fires from effects without user expectation of UI feedback
// (LobbyBackground roomContext effect, usePortraitRequest). When these drop
// under the low-balance gate we stay silent — no token-gate bubble needed.
// User-driven AI sends (everything else in AI_HANDLER_MESSAGE_TYPES) DO need
// the bubble: callers like startCharacterCreation/startLevelUp/startMerchant
// switch the UI optimistically after `send()`, so a silent drop leaves the
// user staring at a spinner with no CTA.
const SILENT_DROP_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  'lobby:request-background',
  'lobby:request-portrait',
]);

interface LobbyCommandDeps {
  send: (message: LobbyClientMessage) => void;
  getLobbyId: () => string | null;
  startAldricChat: () => void;
  startKiraChat: () => void;
  startGroltChat: () => void;
  getSelectedCharacterId: () => string | null;
  getCharCreationCharId: () => string | null;
  setWaitingForResponse: (waiting: boolean) => void;
  showAldricExistingCharacterRefusal: () => void;
}

export interface LobbyCommandApi {
  subscribeLobby: (lobbyId: string) => void;
  unsubscribeLobby: (lobbyId: string) => void;
  joinLobby: (lobbyId: string, password?: string) => void;
  leaveLobby: (lobbyId: string) => void;
  setReady: (lobbyId: string, ready: boolean) => void;
  sendTavianMessage: (message: string) => void;
  sendChatMessage: (roomId: string, message: string) => void;
  startCharacterCreation: () => void;
  startCharacterCreationSession: (hasHistory?: boolean) => void;
  sendAldricMessage: (userInput: string) => void;
  cancelCharacterCreation: (charId: string) => void;
  startLevelUp: (charId: string) => void;
  continueLevelUp: (charId: string, userInput: string) => void;
  sendKiraMessage: (userInput: string) => void;
  startMerchantSession: (charId: string) => void;
  sendGroltMessage: (charId: string, message: string) => void;
  closeMerchantSession: (charId: string) => void;
  startCharacterChat: (targetCharacterId: string, targetCharacterName: string) => void;
  closeCharacterChat: (chatSessionId: string) => void;
  // Friends system
  addFriendFromCharChat: (chatSessionId: string) => void;
  requestFriendsList: () => void;
  removeFriend: (friendUserId: string) => void;
  startFriendDm: (targetUserId: string) => void;
  closeFriendDm: (targetUserId: string) => void;
  createParty: (partyName: string, membershipState?: 'open' | 'closed') => void;
  joinParty: (partyId: string) => void;
  leaveParty: (partyId: string) => void;
  voteOnDecision: (decisionId: string, vote: 'yes' | 'no') => void;
  // Accept / decline a pending party invite from the invite banner. Sends
  // 'lobby:party-invite-accept' / 'lobby:party-invite-decline'; the server
  // replies with lobby:open-party-chat + party updates on accept, or a
  // lobby error toast on failure.
  acceptPartyInvite: (inviteId: string) => void;
  declinePartyInvite: (inviteId: string) => void;
  // Leader-only: switch the party between turn-based and real-time pacing.
  setPartyPacing: (partyId: string, mode: PacingMode) => void;
  startGame: (partyId: string) => void;
  // One-click Begin Your Adventure: the server composes ensure-party ->
  // ensure-module -> start for this user and answers with
  // `lobby:quick-start-result` (+ `lobby:game-started` on started/resumed).
  // `moduleId` is the adventure card the sender picked; omitted (or a party
  // that already has a module) falls back to the server-side default.
  quickStartAdventure: (moduleId?: string) => void;
  requestBackground: (roomContext: BackgroundRoomContext) => void;
  requestPortrait: (characterId: string) => void;
  // `initiatedFromChat` MUST be `true` only when fired from inside the
  // opponent's 1:1 char-chat surface (the duel button rendered in that
  // chat). All other entry points (slash commands, scene-panel modals)
  // MUST pass `false` — backend gates the in-voice NPC chat-LLM
  // rejection redirect on this flag.
  startTrainingDuel: (opponentCharId: string, initiatedFromChat: boolean) => void;
  /// Report the focused chat tab to the lobby server so it can pick
  /// the right `ui.*` / `npc.*` cue and emit `SoundtrackPlay`. The
  /// server is the soundtrack source of truth — the client never
  /// triggers audio playback directly. See
  /// `backend/lobby-server/src/lobby/soundtrack.rs::chat_focus_soundtrack_message`
  /// for the resolver.
  setChatFocus: (tab: string, activeNpc?: string | null) => void;
}

function withLobbyId(deps: LobbyCommandDeps, action: (lobbyId: string) => void): void {
  const lobbyId = deps.getLobbyId();
  if (!lobbyId) {
    console.error('No lobby ID set');
    return;
  }
  action(lobbyId);
}

// Render the canonical Buy Tokens gate bubble in the active room (or 'main'
// fallback when no room is active / before activeChatRoomId is set). Dedup
// against the last bubble so user-driven retries don't stack N copies.
// Shared by both the WS-layer drop and error-events.ts INSUFFICIENT_PM
// handler; the duplication on the latter is intentional belt-and-suspenders
// for the case where the server-side pre-check still fires (stale local
// balance, race between client gate update and a queued send).
function renderLowBalanceGate(): void {
  const state = useLobbyStore.getState();
  const activeRoom = state.chatRooms.find((r) => r.id === state.activeChatRoomId);
  const mainRoom = state.chatRooms.find((r) => r.id === 'main');
  const targetRoom = activeRoom ?? mainRoom;
  if (!targetRoom) {
    return;
  }
  const lastMsg = targetRoom.messages[targetRoom.messages.length - 1];
  if (lastMsg?.type === 'token-gate') {
    return;
  }
  state.addMessage(targetRoom.id, {
    id: `gate-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    senderId: 'tavian',
    senderName: 'Tavian',
    content: LOW_BALANCE_GATE_TEXT,
    timestamp: new Date(),
    type: 'token-gate',
    avatarUrl: getStaffAvatarUrl('tavian-avatar.webp'),
    meta: {
      action_kind: 'buyTokens',
      action_label: LOW_BALANCE_GATE_BUTTON_LABEL,
    },
  });
}

export function createLobbyCommandApi(originalDeps: LobbyCommandDeps): LobbyCommandApi {
  const rawSend = originalDeps.send;
  const sendWithIntentTracking = (message: LobbyClientMessage): void => {
    // Drop AI-handler sends when we know the user is below MIN_PM_BALANCE.
    // The user-facing gate is wired upstream (ChatPanel pre-gate renders
    // the local Buy Tokens bubble before this layer is hit); this catches
    // the *auto-fired* paths — LobbyBackground's roomContext effect,
    // usePortraitRequest — that have no upstream gate and otherwise loop
    // (the rejection mutates store → parent re-renders → effect re-fires).
    // `null` (initial REST hasn't landed) is NOT gated — mirrors mobile's
    // `_shouldGateLowBalance` semantics.
    if (AI_HANDLER_MESSAGE_TYPES.has(message.type)) {
      const balance = usePmBalanceStore.getState().pmBalance;
      if (balance !== null && balance < MIN_PM_BALANCE) {
        if (!SILENT_DROP_MESSAGE_TYPES.has(message.type)) {
          // User-driven AI send dropped — callers (startCharacterCreation,
          // startLevelUp, startMerchantSession, startCharacterChat, etc.)
          // optimistically transition UI after `send()` returns, so a
          // silent drop leaves the user staring at a spinner with no CTA.
          // Render the token-gate bubble in the active room so the Buy
          // Tokens button is right where they're looking. Dedup per room
          // prevents stacking N copies if the user retries.
          renderLowBalanceGate();
        }
        return;
      }
    }
    if (!NON_INTENT_MESSAGE_TYPES.has(message.type)) {
      usePlayerActivityStore.getState().markPlayerIntent();
    }
    rawSend(message);
  };
  const deps: LobbyCommandDeps = { ...originalDeps, send: sendWithIntentTracking };
  return {
    subscribeLobby: (lobbyId) => deps.send({ type: 'lobby:subscribe', lobbyId }),

    unsubscribeLobby: (lobbyId) => deps.send({ type: 'lobby:unsubscribe', lobbyId }),

    joinLobby: (lobbyId, password) => deps.send({ type: 'lobby:join', lobbyId, password }),

    leaveLobby: (lobbyId) => deps.send({ type: 'lobby:leave', lobbyId }),

    setReady: (lobbyId, ready) => deps.send({ type: 'lobby:ready', lobbyId, ready }),

    sendTavianMessage: (message) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:tavian-talk',
          lobbyId,
          message,
        })
      ),

    sendChatMessage: (roomId, message) =>
      withLobbyId(deps, (lobbyId) => {
        if (roomId === RoomTypeEnum.TAVIAN) {
          deps.send({
            type: 'lobby:tavian-talk',
            lobbyId,
            message,
          });
        } else if (roomId.startsWith('party-')) {
          const partyId = roomId.replace('party-', '');
          deps.send({
            type: 'lobby:party-chat',
            lobbyId,
            partyId,
            message,
          });
        } else if (roomId.startsWith('dm-')) {
          const toCharacterId = roomId.replace('dm-', '');
          deps.send({
            type: 'lobby:dm-send',
            lobbyId,
            toCharacterId,
            message,
          });
        } else if (roomId.startsWith('inner-')) {
          deps.setWaitingForResponse(true);
          deps.send({
            type: 'lobby:inner-voice-talk',
            lobbyId,
            message,
          });
        } else if (roomId.startsWith('char-chat-')) {
          const chatSessionId = roomId.replace('char-chat-', '');
          deps.send({
            type: 'lobby:char-chat-send',
            lobbyId,
            chatSessionId,
            message,
          });
        } else if (roomId.startsWith('friend-dm-')) {
          // Account-level friend DM: keyed by partner user id, not chat session
          const targetUserId = roomId.replace('friend-dm-', '');
          deps.send({
            type: 'lobby:friend-dm-send',
            targetUserId,
            message,
          });
        } else {
          deps.send({
            type: 'lobby:chat',
            lobbyId,
            message,
          });
        }
      }),

    startCharacterCreation: () =>
      withLobbyId(deps, (lobbyId) => {
        deps.send({
          type: 'lobby:char-create-start',
          lobbyId,
        });
        deps.startAldricChat();
        deps.setWaitingForResponse(true);
      }),

    // Defaults to hasHistory=true so callers that omit it (direct Aldric click,
    // in-memory reconnect) stay on the safe empty-stream dedupe path. Only the
    // session-state lost-chat resume passes false to request an opener replay.
    startCharacterCreationSession: (hasHistory = true) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:char-create-start',
          lobbyId,
          hasHistory,
        })
      ),

    sendAldricMessage: (userInput) =>
      withLobbyId(deps, (lobbyId) => {
        const selectedCharId = deps.getSelectedCharacterId();
        const charCreationCharId = deps.getCharCreationCharId();

        // Player already has a finalized character — Aldric can't help them
        // create another. Answer in-character client-side instead of hitting
        // the backend "already have a character" guard, which would surface
        // as the generic error-fallback "spirits distant" message.
        if (selectedCharId && !charCreationCharId) {
          deps.showAldricExistingCharacterRefusal();
          return;
        }

        const charId = selectedCharId || charCreationCharId;
        deps.send({
          type: 'lobby:char-create-continue',
          lobbyId,
          charId: charId || undefined,
          userInput,
        });
      }),

    cancelCharacterCreation: (charId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:char-create-cancel',
          lobbyId,
          charId,
        })
      ),

    startLevelUp: (charId) =>
      withLobbyId(deps, (lobbyId) => {
        deps.send({
          type: 'lobby:level-up-start',
          lobbyId,
          charId,
        });
        deps.startKiraChat();
      }),

    continueLevelUp: (charId, userInput) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:level-up-continue',
          lobbyId,
          charId,
          userInput,
        })
      ),

    sendKiraMessage: (userInput) =>
      withLobbyId(deps, (lobbyId) => {
        const charId = deps.getSelectedCharacterId();
        if (!charId) {
          console.error('No character selected for level-up');
          return;
        }
        deps.send({
          type: 'lobby:level-up-continue',
          lobbyId,
          charId,
          userInput,
        });
      }),

    startMerchantSession: (charId) =>
      withLobbyId(deps, (lobbyId) => {
        deps.send({ type: 'lobby:merchant-start', lobbyId, charId });
        deps.startGroltChat();
      }),

    sendGroltMessage: (charId, message) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({ type: 'lobby:merchant-message', lobbyId, charId, message })
      ),

    closeMerchantSession: (charId) =>
      withLobbyId(deps, (lobbyId) => deps.send({ type: 'lobby:merchant-close', lobbyId, charId })),

    startCharacterChat: (targetCharacterId, targetCharacterName) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:char-chat-start',
          lobbyId,
          targetCharacterId,
          targetCharacterName,
        })
      ),

    closeCharacterChat: (chatSessionId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:char-chat-close',
          lobbyId,
          chatSessionId,
        })
      ),

    addFriendFromCharChat: (chatSessionId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:char-chat-add-friend',
          lobbyId,
          chatSessionId,
        })
      ),

    requestFriendsList: () => deps.send({ type: 'lobby:friends-list' }),
    removeFriend: (friendUserId: string) =>
      deps.send({ type: 'lobby:friend-remove', friendUserId }),

    startFriendDm: (targetUserId) => deps.send({ type: 'lobby:friend-dm-start', targetUserId }),

    closeFriendDm: (targetUserId) => deps.send({ type: 'lobby:friend-dm-close', targetUserId }),

    createParty: (partyName, membershipState) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:party-create',
          lobbyId,
          partyName,
          membershipState,
        })
      ),

    joinParty: (partyId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:party-request-join',
          lobbyId,
          partyId,
        })
      ),

    leaveParty: (partyId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:party-leave',
          lobbyId,
          partyId,
        })
      ),

    voteOnDecision: (decisionId, vote) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:party-vote',
          lobbyId,
          decisionId,
          vote,
        })
      ),

    acceptPartyInvite: (inviteId) =>
      deps.send({
        type: 'lobby:party-invite-accept',
        inviteId,
      }),

    declinePartyInvite: (inviteId) =>
      deps.send({
        type: 'lobby:party-invite-decline',
        inviteId,
      }),

    setPartyPacing: (partyId, mode) =>
      deps.send({
        type: 'lobby:party-set-pacing',
        partyId,
        mode,
      }),

    startGame: (partyId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:start-game',
          lobbyId,
          partyId,
        })
      ),

    quickStartAdventure: (moduleId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:quick-start-adventure',
          lobbyId,
          moduleId,
        })
      ),

    requestBackground: (roomContext) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:request-background',
          lobbyId,
          roomContext,
        })
      ),

    requestPortrait: (characterId) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:request-portrait',
          lobbyId,
          characterId,
        })
      ),

    startTrainingDuel: (opponentCharId, initiatedFromChat) =>
      withLobbyId(deps, (lobbyId) =>
        deps.send({
          type: 'lobby:start-training-duel',
          lobbyId,
          opponentCharId,
          initiatedFromChat,
        })
      ),

    setChatFocus: (tab, activeNpc) =>
      // No lobby-id wrapper — chat focus is per-user-session, not
      // per-lobby. The server reads `user_id` off the WS connection
      // itself.
      deps.send({
        type: 'lobby:set-chat-focus',
        tab,
        activeNpc: activeNpc ?? undefined,
      }),
  };
}
